/**
 * Rascunho de formulario da previa.
 *
 * Cada aba edita uma COPIA local do que veio do servidor e so "promove" a copia
 * a referencia quando a pessoa clica em "Salvar na previa". Tres estados
 * derivam disso, e a barra de acoes mostra exatamente um:
 *   - `dirty`  : o rascunho difere da referencia -> "Alteracoes nao salvas";
 *   - `saved`  : ja salvou ao menos uma vez e nao ha diferenca -> "Salvo na previa";
 *   - nenhum   : nunca mexeu.
 *
 * A comparacao e por serializacao JSON. E o suficiente para objetos pequenos e
 * planos como os desta tela; nao serve para estruturas com `Date` ou funcoes,
 * que aqui nao existem (datas sao string ISO por contrato).
 *
 * Nada aqui fala com a rede: a "gravacao" e trocar a referencia. Quando o
 * backend chegar, `salvar` passa a devolver a promessa do `PUT` e a aba mostra
 * `loading` no botao -- a API do hook nao muda.
 */

import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';

export interface Rascunho<T> {
  readonly draft: T;
  readonly setDraft: Dispatch<SetStateAction<T>>;
  /** Atalho para alterar alguns campos de um objeto. */
  readonly patch: (parcial: Partial<T>) => void;
  readonly dirty: boolean;
  readonly saved: boolean;
  readonly salvar: () => void;
  readonly descartar: () => void;
}

export function useRascunho<T extends object>(base: T): Rascunho<T> {
  const [draft, setDraft] = useState<T>(base);
  const [referencia, setReferencia] = useState<T>(base);
  const [jaSalvou, setJaSalvou] = useState(false);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(referencia), [draft, referencia]);

  const patch = useCallback((parcial: Partial<T>): void => {
    setDraft((anterior) => ({ ...anterior, ...parcial }));
  }, []);

  const salvar = useCallback((): void => {
    setReferencia(draft);
    setJaSalvou(true);
  }, [draft]);

  const descartar = useCallback((): void => {
    setDraft(referencia);
  }, [referencia]);

  return { draft, setDraft, patch, dirty, saved: jaSalvou && !dirty, salvar, descartar };
}
